import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  Button,
  Dropdown,
  FormContainer,
  FormItem,
  Input,
  Modal,
  Spinner,
} from 'elmo-elements';

import {
  useGetCurrentTenantProfileQuery,
  useGetVocamChannelsQuery,
  useGetVocamChaptersQuery,
  useGetVocamRegionsQuery,
} from '@/modules/services';
import { FrameDataType } from '@/modules/types/frame';
import { ElearningParamType } from '../../../routes';

import { LayoutSectionComponent } from '@/components/LayoutSection';
import { ContentFormType } from '../SlideForm';
import './SlideSetup.scss';
import { VocamChapterType } from '@/modules/services/videoLibrary';
import { hasValidVocamDataIds } from '@/lib/util';
import VocamForm from './VocamForm';

interface SlideSetupPropsType {
  frameData: FrameDataType;
  form: UseFormReturn<ContentFormType, object>;
  isVideoLibraryModalOpen: boolean;
  browseAssetLibraryHandler: () => void;
  setIsVideoLibraryModalOpen: (isOpen: boolean) => void;
}

const VideoSlideSetup = ({
  isVideoLibraryModalOpen,
  browseAssetLibraryHandler,
  setIsVideoLibraryModalOpen,
  frameData,
  form,
}: SlideSetupPropsType): React.ReactElement => {
  const { frameId } = useParams<ElearningParamType>();

  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = form;

  const vocamMetadata = watch('vocamMetadata') || {};
  const topic = watch('vocamMetadata.topicId') || 0;
  const module = watch('vocamMetadata.moduleId') || 0;
  const bgVideo = watch('bgVideo') || '';
  const bgVideoTitle = watch('bgVideoTitle') || '';

  const vocamRegions = useGetVocamRegionsQuery();
  const { data: tenantProfile, isLoading: tenantProfileIsLoading } =
    useGetCurrentTenantProfileQuery();
  const channels = useGetVocamChannelsQuery();
  const chapters = useGetVocamChaptersQuery(module, {
    skip: !module,
  });

  const handleBgVideoSelect = (currentFileName: string) => {
    setValue('bgVideo', currentFileName);
    // reset vocamMetadata
    if (currentFileName) {
      setValue('vocamMetadata', {});
    }
  };

  const getTopicTitle = (topicId: number, topics: VocamChapterType[]) =>
    topics?.find((chapter) => +chapter.id === +topicId)?.name || '';

  const renderBrowseButton = () => {
    if (tenantProfile?.configMetadata.enableVideoLibrary === 1) {
      return (
        <Dropdown
          text="Browse"
          ariaLabel="Browse video assets"
          className="dropdown-asset"
        >
          <Dropdown.Item
            ariaLabel="Asset Library"
            value="asset"
            onClick={browseAssetLibraryHandler}
          >
            Asset Library
          </Dropdown.Item>
          <Dropdown.Item
            ariaLabel="Video Library"
            value="video"
            onClick={() => {
              setIsVideoLibraryModalOpen(true);
            }}
          >
            Video Library
          </Dropdown.Item>
        </Dropdown>
      );
    }

    return (
      <Button
        ariaLabel="Browse"
        className="video-setup-browse"
        type="default"
        onClick={browseAssetLibraryHandler}
      >
        Browse
      </Button>
    );
  };

  if (!frameId || !frameData) return <div />;
  if (channels.isLoading || vocamRegions.isLoading || tenantProfileIsLoading)
    return <Spinner />;

  return (
    <>
      <Modal
        isOpened={isVideoLibraryModalOpen}
        title="Select video"
        closeModal={() => setIsVideoLibraryModalOpen(false)}
      >
        <VocamForm
          frameData={frameData}
          onSave={() => setIsVideoLibraryModalOpen(false)}
        />
      </Modal>
      <FormContainer maxColumns={2}>
        {frameData.isVideoFrame && (
          <FormItem
            label="Video"
            size="sm"
            message={!topic ? errors.bgVideo?.message : ''}
            status={errors.bgVideo && !topic ? 'error' : 'default'}
          >
            <div className="asset-library-form">
              {!hasValidVocamDataIds(vocamMetadata) && (
                <Input
                  {...register('bgVideo', {})}
                  value={bgVideo}
                  onChange={(event) => handleBgVideoSelect(event.target.value)}
                  ariaLabel="Background video content"
                />
              )}
              {!!hasValidVocamDataIds(vocamMetadata) && (
                <Input
                  isReadOnly={true}
                  value={getTopicTitle(topic, chapters.data ?? [])}
                  ariaLabel="Vocam video content"
                />
              )}
              {renderBrowseButton()}
            </div>
          </FormItem>
        )}
        {frameData.isVideoFrame && !hasValidVocamDataIds(vocamMetadata) && (
          <FormItem
            label="Video Title"
            size="sm"
            message={errors.bgVideoTitle?.message}
            status={errors.bgVideoTitle ? 'error' : 'default'}
          >
            <Input
              {...register('bgVideoTitle', {})}
              value={bgVideoTitle}
              onChange={(event) => setValue('bgVideoTitle', event.target.value)}
              placeholder="Enter video title"
              ariaLabel="Background video title"
            />
          </FormItem>
        )}
      </FormContainer>
    </>
  );
};

const VideoSlideSetupComponent = (
  props: SlideSetupPropsType,
): React.ReactElement => (
  <LayoutSectionComponent
    heading="Video Setup"
    content={<VideoSlideSetup {...props} />}
    isCollapsible={true}
  />
);

export default VideoSlideSetupComponent;
